import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MoreHorizontal, Share2, Link2, ExternalLink, Flag, Trash2, EyeOff, MapPinned, Ban } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ReportButton from "@/components/ReportButton";
import { BlockConfirmDialog } from "@/components/profile/BlockUserButton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const ORIGIN_LABEL = { profile: "Abrir perfil", community: "Abrir comunidade", group: "Abrir grupo" };

export function PostMenu({ item, user, onRemoved }) {
  const nav = useNavigate();
  const [report, setReport] = useState(false);
  const [block, setBlock] = useState(false);
  const [confirm, setConfirm] = useState(null); // "delete" | "hide"
  const [busy, setBusy] = useState(false);
  const isProfile = item.type === "profile";
  const linked = !isProfile && !!item.source_post_id;
  const removeOnly = linked && item.is_mine; // «Retirar desta comunidade»: mantém-se nos outros destinos
  const masterId = isProfile ? item.id : item.source_post_id;
  const url = `${window.location.origin}${item.post_link || item.source?.link || "/feed"}`;
  const canDelete = !!user && item.is_mine;
  const canManage = !!user && item.is_mine && (linked || (isProfile && item.has_destinations));
  const canHide = !!user && !item.is_mine && (isProfile ? user.role === "admin" : !!item.can_moderate);
  const canReport = !!user && !item.is_mine;
  const authorId = item.author?.id || item.author_id;
  const canBlock = canReport && !!authorId && !item.system_tag;
  const originKind = item.source?.type === "community" ? (item.source.kind === "group" ? "group" : "community") : "profile";
  const reportType = isProfile ? "profile_post" : "community_post";

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Ligação copiada"); }
    catch { toast.error("Não foi possível copiar a ligação"); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "AngolaUK+", text: (item.text || "").slice(0, 120), url }); return; }
      catch (e) { if (e?.name === "AbortError") return; }
    }
    copy();
  };
  const run = async () => {
    setBusy(true);
    try {
      if (confirm === "delete") await api.delete(isProfile ? `/profile/posts/${item.id}` : `/community-posts/${item.id}`);
      else await api.post(isProfile ? `/admin/profile-posts/${item.id}/hide` : `/community-posts/${item.id}/moderate/hide`, {});
      toast.success(confirm === "delete" ? "Publicação apagada" : "Publicação ocultada");
      onRemoved?.(item);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro"); }
    finally { setBusy(false); setConfirm(null); }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label="Mais acções" data-testid={`post-menu-${item.type}-${item.id}`}
                  className="h-11 w-11 -mr-2 inline-flex items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 active:bg-zinc-200">
            <MoreHorizontal size={20} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 rounded-xl" data-testid={`post-menu-content-${item.id}`}>
          <DropdownMenuItem onSelect={share} data-testid={`post-menu-share-${item.id}`} className="gap-2 py-2.5"><Share2 size={15} /> Partilhar</DropdownMenuItem>
          <DropdownMenuItem onSelect={copy} data-testid={`post-menu-copy-${item.id}`} className="gap-2 py-2.5"><Link2 size={15} /> Copiar ligação</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => nav(item.source?.link || "/feed")} data-testid={`post-menu-origin-${item.id}`} className="gap-2 py-2.5"><ExternalLink size={15} /> {ORIGIN_LABEL[originKind]}</DropdownMenuItem>
          {(canReport || canDelete || canHide) && <DropdownMenuSeparator />}
          {canReport && <DropdownMenuItem onSelect={() => setReport(true)} data-testid={`post-menu-report-${item.id}`} className="gap-2 py-2.5"><Flag size={15} /> Denunciar</DropdownMenuItem>}
          {canBlock && <DropdownMenuItem onSelect={() => setBlock(true)} data-testid={`post-menu-block-${item.id}`} className="gap-2 py-2.5"><Ban size={15} /> Bloquear {item.author?.name ? item.author.name.split(" ")[0] : "autor"}</DropdownMenuItem>}
          {canManage && <DropdownMenuItem onSelect={() => nav(`/perfil/publicacoes?post=${masterId}`)} data-testid={`post-menu-destinations-${item.id}`} className="gap-2 py-2.5"><MapPinned size={15} /> Gerir destinos</DropdownMenuItem>}
          {canDelete && <DropdownMenuItem onSelect={() => setConfirm("delete")} data-testid={`post-menu-delete-${item.id}`} className="gap-2 py-2.5 text-[#c8102e] focus:text-[#c8102e]"><Trash2 size={15} /> {removeOnly ? "Retirar desta comunidade" : "Apagar publicação"}</DropdownMenuItem>}
          {canHide && <DropdownMenuItem onSelect={() => setConfirm("hide")} data-testid={`post-menu-hide-${item.id}`} className="gap-2 py-2.5"><EyeOff size={15} /> Ocultar (moderação)</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>

      {canReport && <ReportButton target_type={reportType} target_id={item.id} hideTrigger open={report} onOpenChange={setReport} />}
      {canBlock && <BlockConfirmDialog open={block} onOpenChange={setBlock} userId={authorId} userName={item.author?.name} onBlocked={() => onRemoved?.(item)} />}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && !busy && setConfirm(null)}>
        <AlertDialogContent className="rounded-2xl max-w-sm" data-testid={`post-confirm-${item.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "delete" ? (removeOnly ? "Retirar desta comunidade?" : "Apagar esta publicação?") : "Ocultar esta publicação?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "delete"
                ? (removeOnly ? "A publicação deixa de aparecer nesta comunidade, com os seus comentários e reações. Mantém-se nos outros destinos." : "Esta acção é definitiva e remove a publicação de todos os destinos. Comentários e reacções associados deixam de estar visíveis.")
                : "A publicação deixa de ser visível para os membros. O autor será notificado quando aplicável."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid={`post-confirm-cancel-${item.id}`} className="rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy} data-testid={`post-confirm-yes-${item.id}`} className="rounded-xl bg-[#c8102e] hover:bg-[#a50c24]">
              {busy ? "A processar…" : confirm === "delete" ? (removeOnly ? "Retirar" : "Apagar") : "Ocultar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
